/* eslint-disable id-blacklist */
import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { LayerOrientation, PcbNodeTypes, } from "@buildwithflux/models";
import { metersToMm, naturalSortByProperty, normalizeDegreeOrientation, normalizeRadians, radiansToDegrees, roundTo, } from "@buildwithflux/shared";
import { pick } from "lodash";
import { Box3, Vector3 } from "three";
import { ElementHelper } from "../../../elements";
import { getDisplayed5DigitChangeId, ThreeHelpers } from "../../../helpers";
import { getDescendantsOfType, isDescendantOfLayout, PcbNodeHelper } from "../../../nodes";
import { stringifyPropertyValue } from "../../../pcb";
import { areOldProperties, getDefaultValueProperty, getPropertyValue } from "../../../property";
import { BaseExporter } from "../BaseExporter";
/**
 * Temporary solution for angle decimal precision issues.
 * TODO: Remove once we stop truncating radians.
 */
function fixAngleDecimals(angle, maxDecimals = 2) {
    return Number(angle.toFixed(maxDecimals));
}
const Formats = {
    OpenPNP: {
        // Based on https://github.com/openpnp/openpnp/wiki/Importing-Centroid-Data#others
        // and Riley Porter's KiCad export screenshot at
        // https://feedback.flux.ai/admin/feedback/featurerequests/p/openpnp-export-csv-file-support
        columns: {
            Designator: "Ref",
            Value: "Val",
            Package: "Package",
            "Mid X": "PosX",
            "Mid Y": "PosY",
            Rotation: "Rot",
            Layer: "Side",
        },
    },
    JLCPCB: {
        columns: {
            Designator: "Designator",
            Layer: "Layer",
            Rotation: "Rotation",
            "Mid X": "Mid X",
            "Mid Y": "Mid Y",
        },
    },
};
/**
 * PickAndPlaceExporter - Pure business logic for Pick and Place CSV generation.
 * This class contains the core data transformation logic for generating Pick and Place data.
 * Browser-specific download functionality should be handled by frontend adapters.
 */
export class PickAndPlaceExporter extends BaseExporter {
    pcbLayoutNodes;
    elements;
    constructor(documentData, documentOwner, pcbLayoutNodes) {
        super(documentData, documentOwner);
        this.pcbLayoutNodes = pcbLayoutNodes;
        this.elements = documentData.elements;
    }
    // QUESTION: should we also filter by isValidPickAndPlaceElement
    static hasThingsToExport(pcbLayoutNodesMap) {
        return Boolean(Object.values(pcbLayoutNodesMap).find((node) => node.type === PcbNodeTypes.element));
    }
    static getElementNodes(pcbLayoutNodesMap) {
        const pcbLayoutNodes = Object.values(pcbLayoutNodesMap);
        return PcbNodeHelper.filterNodesOfType(pcbLayoutNodes, PcbNodeTypes.element).filter((node) => isDescendantOfLayout(pcbLayoutNodesMap, node.uid));
    }
    /**
     * Creates the Pick and Place data.
     * This is the pure business logic that can be used in both frontend and backend.
     */
    // Specs via https://support.jlcpcb.com/article/79-pick-place-file-for-smt-assembly
    createPickAndPlaceData() {
        const data = [];
        const elementNodes = PickAndPlaceExporter.getElementNodes(this.pcbLayoutNodes);
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        elementNodes.forEach((elementNode) => {
            if (PcbNodeHelper.isNodeExclusiveSubLayoutContainer(elementNode, this.pcbLayoutNodes)) {
                // We don't want any containers in the pick-and-place file and
                // unfortunately the sublayout "root" is of type element
                return;
            }
            const element = ElementHelper.getNestedElementData(this.elements, elementNode.uid);
            if (!element || ElementHelper.isExcludedFromBOM(element)) {
                // NOTE: elements can have exclude_from_pcb, so be absent from the nodes
                // QUESTION: are there other valid reasons to be absent?
                return;
            }
            const { position, rotation } = this.getFootprintCentroidPosition(elementNode);
            const { designator, layer } = this.getFootprintParentElementFields(elementNode);
            const valueProperty = getDefaultValueProperty(element.properties ?? {});
            const value = valueProperty ? stringifyPropertyValue(valueProperty) : "";
            const properties = element.properties ?? {};
            let packageCode;
            if (areOldProperties(properties)) {
                const propValue = properties[defaultPropertyDefinitions.package_case_code.key];
                packageCode = typeof propValue === "string" ? propValue : "";
            }
            else {
                const rawPackageCode = getPropertyValue(defaultPropertyDefinitions.package_case_code.label, properties) ??
                    getPropertyValue("Package", properties);
                packageCode = typeof rawPackageCode === "string" ? rawPackageCode : "";
            }
            const dataRow = {
                Designator: designator || "",
                "Mid X": `${roundTo(metersToMm(position.x), 4)}mm`,
                "Mid Y": `${roundTo(metersToMm(position.y), 4)}mm`,
                Layer: layer === LayerOrientation.top ? "Top" : "Bottom",
                Rotation: fixAngleDecimals(normalizeDegreeOrientation(radiansToDegrees(rotation))),
                Package: packageCode,
                Value: value,
            };
            data.push(dataRow);
        });
        return data.sort(naturalSortByProperty("Designator"));
    }
    /**
     * Translates the data to a specific format (e.g., OpenPNP, JLCPCB).
     */
    translateFormat(data, formatName) {
        const format = Formats[formatName];
        return data.map((row) => {
            const picked = pick(row, Object.keys(format.columns));
            const result = {};
            for (const [key, value] of Object.entries(picked)) {
                const newKey = format.columns[key];
                if (newKey && value !== undefined) {
                    result[newKey] = value;
                }
            }
            return result;
        });
    }
    /**
     * Gets the available format names.
     */
    getFormatNames() {
        return Object.keys(Formats);
    }
    /**
     * Creates a filename for the Pick and Place export.
     */
    createPickAndPlaceFileName(versionShortCode) {
        const postfix = versionShortCode ? `Pick and Place - Version ${versionShortCode}` : "Pick and Place";
        return this.createFileName(undefined, postfix);
    }
    /**
     * Gets the version short code from the document data.
     */
    getVersionShortCode() {
        return this.documentData.latestActionRecordUid
            ? getDisplayed5DigitChangeId(this.documentData.latestActionRecordUid)
            : undefined;
    }
    getFootprintParentElementFields(elementNode) {
        if (!elementNode.bakedRules) {
            return { designator: undefined, layer: undefined };
        }
        return { designator: elementNode.name, layer: elementNode.bakedRules.layer };
    }
    getFootprintCentroidPosition(elementNode) {
        if (!elementNode.bakedRules) {
            throw new Error("footprintNode must have bakedRules");
        }
        const elementPads = getDescendantsOfType(this.pcbLayoutNodes, elementNode, PcbNodeTypes.pad);
        const elementRotation = ThreeHelpers.getVector3FromIVector3(elementNode.bakedRules.rotationRelativeToRootLayout ?? { x: 0, y: 0, z: 0 });
        const elementPosition = ThreeHelpers.getVector3FromIVector3(elementNode.bakedRules.positionRelativeToRootLayout ?? { x: 0, y: 0, z: 0 });
        // Get pad positions relative to the element's origin. This would often be equal to pad.bakedRules.position,
        // but not necessarily, because the pad might have repositioned parents within element.
        const padPositions = elementPads
            .filter((p) => p.bakedRules?.positionRelativeToRootLayout)
            .map((p) => ThreeHelpers.getVector3FromIVector3(p.bakedRules.positionRelativeToRootLayout)
            .sub(elementPosition)
            .applyEuler(ThreeHelpers.getEulerInverse(ThreeHelpers.getEulerFromIVector3(elementRotation))));
        const bounds = new Box3().setFromPoints(padPositions);
        // Note, this is not the polygon centroid or the centroid of the points (i.e. average position);
        // it's the rectangular bounding box center. This appears to be what JLCPCB expects,
        // and is similar to Altium's method here:
        // https://www.altium.com/documentation/knowledge-base/altium-designer/calculate-component-pick-and-place-center-of-a-footprint
        const centroid = bounds.getCenter(new Vector3());
        centroid.applyEuler(ThreeHelpers.getEulerFromIVector3(elementRotation)).add(elementPosition);
        let zRotation = elementRotation.z;
        // rotationRelativeToRootLayout can end up with either the x or the y axis flipped,
        // even if we only flipped around the y axis in the code, because the inverse world-space
        // transformation has multiple possible answers (eg y=180,z=45 is equivalent to x=180,z=135).
        // So we check both.
        const normX = normalizeRadians(elementRotation.x, true);
        const xFlip = Math.abs(normX) > Math.PI / 2;
        if (xFlip) {
            zRotation = zRotation - Math.PI;
        }
        // Old JLCPCB treated bottom-layer rotation as the negative of Flux's value. However, this
        // switched polarity (!). So now the rotation is the same as Flux's absolute value.
        // We implemented the old behaviour in https://github.com/buildwithflux/flux-app/pull/2079
        // but changed to the new behaviour in Oct 2023.
        //
        // This change was noted by JLCKicadTools in 2022 July here:
        // https://github.com/matthewlai/JLCKicadTools/blob/master/jlc_kicad_tools/jlc_lib/cpl_fix_rotations.py#L166
        //
        // The new behaviour seems to match what's documented elsewhere, for example:
        // http://numerical-help-guide.s3.amazonaws.com/understanding-the-centroid-file-r2-2.pdf
        //
        // Old behaviour would require the following line here to invert bottom-layer rotation:
        // if (xFlip !== yFlip) zRotation *= -1;
        return {
            position: { x: centroid.x, y: centroid.y },
            rotation: zRotation,
        };
    }
}
//# sourceMappingURL=PickAndPlaceExporter.js.map